import React from "react";
import { FaInstagram, FaFacebook, FaYoutube, FaMapMarkerAlt, FaEnvelope, FaPhone } from "react-icons/fa";
import { useTranslation } from "react-i18next";

function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-orange-500 dark:bg-slate-900 text-white dark:text-slate-100 border-t border-orange-600 dark:border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          <div className="text-center md:text-left min-w-0">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                <img src="/logo192.png" alt="" className="w-10 h-10 object-contain" />
              </div>

              <h3 className="text-base sm:text-lg font-semibold text-orange-100 dark:text-slate-300 text-left max-w-[14rem] sm:max-w-none">
                {t("aboutTitle")}
              </h3>
            </div>
            <p className="text-orange-100 dark:text-slate-300 leading-relaxed text-sm">{t("about")}</p>
            <div className="mt-4 space-y-3 text-sm text-orange-100 dark:text-slate-300">
              <div className="flex items-start justify-center md:justify-start gap-2">
                <span className="text-base leading-none shrink-0">💼</span>
                <p className="leading-relaxed text-left">
                  <span className="font-semibold">{t("professional")}</span> — {t("professionalDesc")}
                </p>
              </div>
              <div className="flex items-start justify-center md:justify-start gap-2">
                <span className="text-base leading-none shrink-0">⚡</span>
                <p className="leading-relaxed text-left">
                  <span className="font-semibold">{t("fast")}</span> — {t("fastDesc")}
                </p>
              </div>
              <div className="flex items-start justify-center md:justify-start gap-2">
                <span className="text-base leading-none shrink-0">🎯</span>
                <p className="leading-relaxed text-left">
                  <span className="font-semibold">{t("accurate")}</span> — {t("accurateDesc")}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center md:text-left min-w-0">
            <h4 className="text-lg font-semibold mb-4">{t("contactInfo")}</h4>
            <div className="space-y-3">
              <div className="flex flex-wrap items-start justify-center md:justify-start gap-2 text-left">
                <FaMapMarkerAlt className="text-blue-400 shrink-0 mt-0.5" />
                <span className="text-orange-100 dark:text-slate-300 break-words">{t("footer.locationDetail")}</span>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                <FaEnvelope className="text-blue-400 shrink-0" />
                <a
                  href="mailto:info.businessfinancepk@gmail.com"
                  className="text-orange-100 dark:text-slate-300 hover:text-white dark:hover:text-white transition-colors break-all text-left"
                >
                  info.businessfinancepk@gmail.com
                </a>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                <FaPhone className="text-blue-400 shrink-0 transform scale-x-[-1]" />
                <a
                  href="tel:+923145832291"
                  className="text-orange-100 dark:text-slate-300 hover:text-white dark:hover:text-white transition-colors"
                >
                  +92 314 5832291
                </a>
              </div>
            </div>
            <div className="flex justify-center md:justify-start space-x-4 mt-5">
              <a
                href="https://instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <FaInstagram className="text-white text-xl group-hover:animate-pulse" />
              </a>
              <a
                href="https://facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <FaFacebook className="text-white text-xl group-hover:animate-pulse" />
              </a>
              <a
                href="https://youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-full hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <FaYoutube className="text-white text-xl group-hover:animate-pulse" />
              </a>
            </div>
          </div>

          <div className="text-center md:text-left min-w-0">
            <h4 className="text-lg font-semibold mb-4">{t("footer.locationMap")}</h4>
            <div className="rounded-xl overflow-hidden border border-orange-300 dark:border-slate-700 shadow-md">
              <iframe
                title={t("supportPage.mapTitle")}
                src="https://www.google.com/maps?q=Khairpur%20Mirs,%20Sindh,%20Pakistan&output=embed"
                width="100%"
                height="170"
                className="block w-full min-h-[160px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="text-center md:text-left min-w-0">
            <h4 className="text-lg font-semibold mb-4">{t("footer.companyExperience")}</h4>
            <p className="text-orange-100 dark:text-slate-300 text-sm leading-relaxed">{t("footer.companyExperienceText")}</p>
          </div>
        </div>

        <div className="border-t border-orange-400 dark:border-slate-700 mt-8 pt-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
            <div className="text-left min-w-0 flex-1">
              <div className="flex flex-col sm:flex-wrap sm:flex-row items-stretch sm:items-center gap-x-5 gap-y-3 text-sm text-orange-100 dark:text-slate-300">
                <details className="group min-w-0">
                  <summary className="cursor-pointer font-medium hover:text-white">{t("footer.privacyPolicy")}</summary>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-orange-100/90 dark:text-slate-400">{t("footer.privacyContent")}</p>
                </details>
                <details className="group min-w-0">
                  <summary className="cursor-pointer font-medium hover:text-white">{t("footer.termsConditions")}</summary>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-orange-100/90 dark:text-slate-400">{t("footer.termsContent")}</p>
                </details>
                <details className="group min-w-0">
                  <summary className="cursor-pointer font-medium hover:text-white">{t("footer.sitemap")}</summary>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-orange-100/90 dark:text-slate-400">{t("footer.sitemapContent")}</p>
                </details>
                <details className="group min-w-0">
                  <summary className="cursor-pointer font-medium hover:text-white">{t("footer.cookiePolicy")}</summary>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-orange-100/90 dark:text-slate-400">{t("footer.cookieContent")}</p>
                </details>
              </div>
            </div>
            <p className="text-sm lg:text-right text-orange-100 dark:text-slate-400 shrink-0 max-w-full break-words">
              {t("footer.footerTagline")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
